import Link from 'next/link'
import { FC } from 'react'

import { Minus, Plus, SubmitButton } from '@syconium/little-miss-figgy'

import { PriceDisplay } from '../../../../../brunswick/components/PriceDisplay'
import { VariantAttributeSelectors } from '../../../../../brunswick/components/VariantAttributeSelectors/VariantAttributeSelectors'
import { trackEvent } from '../../../../../lib/analytics'
import { bulkOrderCategory, viewProductAction } from '../../constants'
import { LinkWhen } from '../LinkWhen'

import { BagSoFar } from './BagSoFar'
import { BulkQuantity } from './BulkQuantity'
import { ButtonWhen } from './ButtonWhen'
import {
  ButtonCtaWrapper,
  Contents,
  DesktopBriefDescription,
  MobileBriefDescription,
  MobilePlusMinus,
  PriceWrapper,
  ProductTitle,
  Tile,
  TileImage,
  TileRowWrapper,
  TileText,
  VariantAttributeSelectorsWrapper,
  ViewProductMobile,
} from './styles'
import { ViewModel } from './types'

export const Template: FC<ViewModel> = ({
  availableFitsKeys,
  briefProductDescription,
  category,
  colorGroups,
  ctaButton,
  currency,
  currentFitKey,
  currentSize,
  discountPrice,
  discountPriceExplicitFmt,
  fitNames,
  handleAddToCart,
  imageSource,
  isActiveProductWaitlistable,
  isCurrentColorCompletelySoldOut,
  onProductView,
  pdpHref,
  price,
  priceExplicitFmt,
  priceRange,
  product,
  productGroupHandle,
  productGroupTitle,
  selectedColor,
  setSelectedColor,
  setSelectedFitKey,
  setSelectedSize,
  sizes,
  sizeSectionTitle,
  userHasAttemptedInvalidAddToBag,
  quantity,
  incrementQuantity,
  decrementQuantity,
  onQuantityChange,
  showMobileContents,
  toggleMobileContents,
  viewProductText,
  status,
  pendingLabel,
  resolvedLabel,
}) => {
  return (
    <Tile>
      <ButtonWhen lessThan='lg' onClick={toggleMobileContents} aria-expanded={showMobileContents}>
        <TileRowWrapper>
          {imageSource && (
            <LinkWhen
              greaterThanOrEqual='lg'
              href={pdpHref}
              onClick={onProductView}
              {...trackEvent({ category: bulkOrderCategory, action: viewProductAction })}
            >
              <TileImage
                className='flex'
                alt={''}
                aspectRatios={{
                  sm: 1,
                  md: 1,
                  lg: 0.8,
                }}
                src={imageSource}
                loading={'lazy'}
                widths={{
                  unit: 'px',
                  sm: 85,
                  md: 85,
                  lg: 250, // We render at 200 width, but our height is 250 and these files are 1:1. So being safe here.
                }}
              />
            </LinkWhen>
          )}

          <TileText>
            <LinkWhen
              greaterThanOrEqual='lg'
              href={pdpHref}
              onClick={onProductView}
              {...trackEvent({ category: bulkOrderCategory, action: viewProductAction })}
            >
              <ProductTitle>{productGroupTitle}</ProductTitle>
            </LinkWhen>
            <PriceWrapper>
              <PriceDisplay
                {...{
                  currency,
                  discountPrice,
                  discountPriceExplicitFmt,
                  price,
                  priceExplicitFmt,
                  priceRange,
                }}
              />
            </PriceWrapper>

            {briefProductDescription && (
              <DesktopBriefDescription>{briefProductDescription}</DesktopBriefDescription>
            )}
          </TileText>
          <MobilePlusMinus>
            {showMobileContents ? (
              <Minus width='16px' height='16px' />
            ) : (
              <Plus width='16px' height='16px' />
            )}
          </MobilePlusMinus>
        </TileRowWrapper>
      </ButtonWhen>

      <Contents $showMobile={showMobileContents}>
        <ViewProductMobile>
          <Link
            {...trackEvent({ category: bulkOrderCategory, action: viewProductAction })}
            href={pdpHref}
            onClick={onProductView}
          >
            {viewProductText}
          </Link>
        </ViewProductMobile>
        {briefProductDescription && (
          <MobileBriefDescription>{briefProductDescription}</MobileBriefDescription>
        )}
        <VariantAttributeSelectorsWrapper>
          <VariantAttributeSelectors
            {...{
              analyticsContext: { category: bulkOrderCategory },
              availableFitsKeys,
              category,
              colorGroups,
              currentFitKey,
              currentSize,
              fitNames,
              isActiveProductWaitlistable,
              isCurrentColorCompletelySoldOut,
              productGroupTitle,
              selectedColor,
              setSelectedColor,
              setSelectedFitKey,
              setSelectedSize,
              sizes,
              sizeSectionTitle,
              userHasAttemptedInvalidAddToBag,
            }}
            sizeChart={undefined}
            openSizeChartModal={() => {}}
          />
        </VariantAttributeSelectorsWrapper>

        <ButtonCtaWrapper>
          <BulkQuantity
            productId={product.id}
            decrementQuantity={decrementQuantity}
            incrementQuantity={incrementQuantity}
            onQuantityChange={onQuantityChange}
            quantity={quantity}
          />
          <SubmitButton
            pendingLabel={pendingLabel}
            resolvedLabel={resolvedLabel}
            status={status}
            block
            disabled={ctaButton.disabled || !quantity || quantity === 0 || quantity < 0}
            onClick={handleAddToCart}
            variant={ctaButton.variant}
            {...trackEvent({
              category: bulkOrderCategory,
              action: 'add to bag',
              value: quantity,
            })}
            idleChildren={ctaButton.text}
          />
          <BagSoFar productGroupHandle={productGroupHandle} />
        </ButtonCtaWrapper>
      </Contents>
    </Tile>
  )
}
